/**
 * DV-INF-002: Demo Viewer Query Client
 *
 * Centralized React Query configuration for Demo Viewer with:
 * - Polling intervals optimized for different data types
 * - Cache invalidation patterns for WebSocket hints
 * - Error handling integrated with demo error system
 */

import { QueryClient } from '@tanstack/react-query';
import { getDemoError } from './demo-error-handler';

/**
 * Demo Viewer specific query keys for consistent cache management
 * Centralized builders ensure WebSocket invalidation targets exact keys
 */
class DemoQueryKeyBuilder {
  private static readonly ROOT = 'demo';

  // Feature flags (rarely change, long cache)
  static featureFlags = () => [this.ROOT, 'feature-flags'] as const;

  // Billing data (changes with new commits)
  static billingCommits = (executionId: string) => [this.ROOT, 'billing', 'commits', executionId] as const;
  static billingCalls = (commitId: string) => [this.ROOT, 'billing', 'calls', commitId] as const;
  static billingApiCallsAggregate = (executionId: string, commitIds: string[]) =>
    [this.ROOT, 'billing', 'api-calls-aggregate', executionId, commitIds] as const;

  // Storage/outputs (changes when stages complete)
  static stageOutputs = (executionId: string) => [this.ROOT, 'storage', 'outputs', executionId] as const;

  // Temporal events (frequently updating during execution)
  static temporalEvents = (executionId: string, page?: number, filters?: any) =>
    [this.ROOT, 'temporal', 'events', executionId, page, filters] as const;

  // Heartbeats (most frequently updating)
  static temporalHeartbeats = (executionId: string) => [this.ROOT, 'temporal', 'heartbeats', executionId] as const;

  // Auth session (moderate update frequency)
  static authSession = () => [this.ROOT, 'auth', 'session'] as const;

  // Cookie debug (immediate/manual only)
  static cookiesEcho = () => [this.ROOT, 'cookies', 'echo'] as const;

  // Stage Tester samples (moderate update frequency, cached per stage)
  static stageSamples = (stage: string) => [this.ROOT, 'stage-tester', 'samples', stage] as const;

  // Invalidation patterns for WebSocket hints
  static getAllForExecution = (executionId: string) => ({
    billingCommits: this.billingCommits(executionId),
    billingApiCallsAggregate: [this.ROOT, 'billing', 'api-calls-aggregate', executionId],
    stageOutputs: this.stageOutputs(executionId),
    temporalEvents: [this.ROOT, 'temporal', 'events', executionId],
    temporalHeartbeats: this.temporalHeartbeats(executionId),
  });

  static getAllBilling = () => [this.ROOT, 'billing'];
  static getAllTemporal = () => [this.ROOT, 'temporal'];
}

// Export both class and convenience object for backward compatibility
export const demoQueryKeys = {
  featureFlags: DemoQueryKeyBuilder.featureFlags(),
  billingCommits: DemoQueryKeyBuilder.billingCommits,
  billingCalls: DemoQueryKeyBuilder.billingCalls,
  billingApiCallsAggregate: DemoQueryKeyBuilder.billingApiCallsAggregate,
  stageOutputs: DemoQueryKeyBuilder.stageOutputs,
  temporalEvents: DemoQueryKeyBuilder.temporalEvents,
  temporalHeartbeats: DemoQueryKeyBuilder.temporalHeartbeats,
  authSession: DemoQueryKeyBuilder.authSession(),
  cookiesEcho: DemoQueryKeyBuilder.cookiesEcho(),
  stageSamples: DemoQueryKeyBuilder.stageSamples,
  // Patterns for invalidation
  execution: DemoQueryKeyBuilder.getAllForExecution,
  billing: DemoQueryKeyBuilder.getAllBilling,
  temporal: DemoQueryKeyBuilder.getAllTemporal,
} as const;

/**
 * Polling intervals optimized for different data update frequencies
 * All intervals have minimum of 1000ms and include jitter to prevent thundering herd
 */
const BASE_INTERVALS = {
  // Feature flags change rarely - long interval
  featureFlags: 30000, // 30 seconds

  // Billing commits change with pipeline execution - medium interval
  billingCommits: 10000, // 10 seconds
  billingCalls: 15000, // 15 seconds (less frequent than commits)

  // Storage outputs change when stages complete - medium interval
  stageOutputs: 8000, // 8 seconds

  // Temporal events update frequently during execution - short interval
  temporalEvents: 3000, // 3 seconds

  // Heartbeats are most frequent - shortest interval
  temporalHeartbeats: 2000, // 2 seconds

  // Auth session moderate updates
  authSession: 20000, // 20 seconds

  // Cookie debug manual only
  cookiesEcho: null, // No automatic polling

  // Stage samples are static - no polling needed
  stageSamples: null, // No automatic polling
} as const;

/**
 * Add jitter to polling interval to prevent thundering herd
 * Minimum interval is 1000ms, jitter is ±10%
 */
function addPollingJitter(baseInterval: number | null): number | false {
  if (baseInterval === null) return false;

  const minInterval = Math.max(baseInterval, 1000); // Enforce 1s minimum
  const jitterRange = minInterval * 0.1; // ±10% jitter
  const jitter = (Math.random() - 0.5) * 2 * jitterRange;

  return Math.round(minInterval + jitter);
}

export const demoPollingIntervals = {
  featureFlags: addPollingJitter(BASE_INTERVALS.featureFlags),
  billingCommits: addPollingJitter(BASE_INTERVALS.billingCommits),
  billingCalls: addPollingJitter(BASE_INTERVALS.billingCalls),
  stageOutputs: addPollingJitter(BASE_INTERVALS.stageOutputs),
  temporalEvents: addPollingJitter(BASE_INTERVALS.temporalEvents),
  temporalHeartbeats: addPollingJitter(BASE_INTERVALS.temporalHeartbeats),
  authSession: addPollingJitter(BASE_INTERVALS.authSession),
  cookiesEcho: addPollingJitter(BASE_INTERVALS.cookiesEcho),
  stageSamples: addPollingJitter(BASE_INTERVALS.stageSamples),
} as const;

/**
 * Create Demo Viewer specific Query Client with optimized defaults
 */
export function createDemoQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default polling interval (can be overridden per query)
        refetchInterval: false, // Disable by default, enable per-query

        // Background refetching when window regains focus
        refetchOnWindowFocus: true,

        // Retry failed requests with exponential backoff
        retry: (failureCount, error) => {
          // Don't retry auth errors (403/401)
          const demoError = getDemoError(error);
          if (demoError?.type === 'permission_denied') {
            return false;
          }

          // Retry up to 3 times for other errors
          return failureCount < 3;
        },

        // Retry delay with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Stale time - how long data is considered fresh
        staleTime: 30000, // 30 seconds default

        // Garbage collection time - how long unused data stays in cache
        gcTime: 300000, // 5 minutes

        // Refetch on network reconnect
        refetchOnReconnect: true,
      },
      mutations: {
        // Retry mutations only for network errors, not validation errors
        retry: (failureCount, error) => {
          const demoError = getDemoError(error);
          return demoError?.type === 'network_error' && failureCount < 2;
        },
      },
    },
  });
}

/**
 * Cache invalidation helpers for WebSocket hints
 */
export const demoCacheInvalidation = {
  /**
   * Invalidate execution-related queries when execution updates
   */
  invalidateExecution: (queryClient: QueryClient, executionId: string) => {
    queryClient.invalidateQueries({
      queryKey: ['demo', 'billing', 'commits', executionId]
    });
    queryClient.invalidateQueries({
      queryKey: ['demo', 'storage', 'outputs', executionId]
    });
    queryClient.invalidateQueries({
      queryKey: ['demo', 'temporal', 'events', executionId]
    });
    queryClient.invalidateQueries({
      queryKey: ['demo', 'temporal', 'heartbeats', executionId]
    });
  },

  /**
   * Invalidate stage-related queries when stage updates
   */
  invalidateStage: (queryClient: QueryClient, executionId: string, _stageName: string) => {
    queryClient.invalidateQueries({
      queryKey: ['demo', 'storage', 'outputs', executionId]
    });
    queryClient.invalidateQueries({
      queryKey: ['demo', 'temporal', 'events', executionId]
    });
  },

  /**
   * Invalidate pipeline-wide queries when pipeline events occur
   */
  invalidatePipeline: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      queryKey: ['demo', 'feature-flags']
    });
    queryClient.invalidateQueries({
      queryKey: ['demo', 'billing']
    });
  },

  /**
   * Force refresh specific query (for manual refresh buttons)
   */
  refetchQuery: (queryClient: QueryClient, queryKey: readonly unknown[]) => {
    queryClient.invalidateQueries({ queryKey });
    return queryClient.refetchQueries({ queryKey });
  },
};

/**
 * Demo Viewer query options presets
 */
export const demoQueryOptions = {
  /**
   * Options for feature flags query
   */
  featureFlags: () => ({
    queryKey: demoQueryKeys.featureFlags,
    staleTime: 60000, // 1 minute (flags change rarely)
    refetchInterval: demoPollingIntervals.featureFlags,
  }),

  /**
   * Options for billing commits query
   */
  billingCommits: (executionId: string) => ({
    queryKey: demoQueryKeys.billingCommits(executionId),
    staleTime: 5000, // 5 seconds (billing updates during execution)
    refetchInterval: demoPollingIntervals.billingCommits,
    enabled: !!executionId, // Only run if executionId provided
  }),

  /**
   * Options for temporal events query with pagination
   */
  temporalEvents: (executionId: string, page = 1, filters?: any) => ({
    queryKey: demoQueryKeys.temporalEvents(executionId, page, filters),
    staleTime: 1000, // 1 second (events update frequently)
    refetchInterval: demoPollingIntervals.temporalEvents,
    enabled: !!executionId,
    // Keep previous data when paginating
    placeholderData: (previousData: any) => previousData,
  }),

  /**
   * Options for heartbeats query (most frequent)
   */
  temporalHeartbeats: (executionId: string) => ({
    queryKey: demoQueryKeys.temporalHeartbeats(executionId),
    staleTime: 500, // 500ms (heartbeats are very frequent)
    refetchInterval: demoPollingIntervals.temporalHeartbeats,
    enabled: !!executionId,
  }),

  /**
   * Options for auth session query
   */
  authSession: () => ({
    queryKey: demoQueryKeys.authSession,
    staleTime: 10000, // 10 seconds (session changes moderately)
    refetchInterval: demoPollingIntervals.authSession,
  }),

  /**
   * Options for one-time queries (no polling)
   */
  cookiesEcho: () => ({
    queryKey: demoQueryKeys.cookiesEcho,
    staleTime: 0, // Always fresh for debugging
    refetchInterval: false, // No automatic polling
  }),

  /**
   * Options for stage samples query (load once, no polling)
   */
  stageSamples: (stage: string) => ({
    queryKey: demoQueryKeys.stageSamples(stage),
    staleTime: Infinity, // Samples are static - never stale
    refetchInterval: false, // No automatic polling
    refetchOnWindowFocus: false, // Don't refetch on focus
    enabled: !!stage, // Only run if stage provided
  }),
};

/**
 * Live mode overrides for higher frequency polling
 */
export const demoLiveModeOptions = {
  temporalEvents: (executionId: string, page = 1, filters?: any) => ({
    ...demoQueryOptions.temporalEvents(executionId, page, filters),
    refetchInterval: 1000, // 1 second in live mode
    staleTime: 100, // Very fresh data
  }),

  temporalHeartbeats: (executionId: string) => ({
    ...demoQueryOptions.temporalHeartbeats(executionId),
    refetchInterval: 500, // 500ms in live mode
    staleTime: 50, // Very fresh data
  }),
};

/**
 * Demo Viewer specific error boundary integration
 */
export function isDemoQueryError(error: unknown): boolean {
  return getDemoError(error) !== null;
}

/**
 * Extract user-friendly error message for Demo Viewer
 */
export function getDemoQueryErrorMessage(error: unknown): string {
  const demoError = getDemoError(error);
  return demoError?.userMessage || 'An unexpected error occurred';
}